<template>
  <div
    ref="wrapper"
    class="cselect"
    :class="{ 'cselect--open': isOpen, 'cselect--disabled': disabled }"
  >
    <button
      type="button"
      class="cselect__trigger"
      :disabled="disabled"
      @click="toggle"
      @keydown="onKeydown"
    >
      <span class="cselect__value" :class="{ 'cselect__value--placeholder': !hasValue }">
        {{ displayLabel }}
      </span>
      <span class="cselect__icon" aria-hidden="true">
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width="14"
          height="14"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
        >
          <polyline points="6 9 12 15 18 9" />
        </svg>
      </span>
    </button>

    <transition name="cselect-drop">
      <div v-if="isOpen" class="cselect__menu">
        <div v-if="searchable" class="cselect__search-wrap">
          <input
            ref="searchInput"
            v-model="searchQuery"
            class="cselect__search"
            placeholder="Search…"
            type="text"
            @keydown.stop
          />
        </div>

        <div class="cselect__options">
          <div
            v-for="opt in filteredOptions"
            :key="String(opt.value)"
            class="cselect__option"
            :class="{ 'cselect__option--selected': isSelected(opt.value) }"
            @click="selectOption(opt)"
          >
            <span class="cselect__option-check" aria-hidden="true">
              <svg
                v-if="isSelected(opt.value)"
                xmlns="http://www.w3.org/2000/svg"
                width="12"
                height="12"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="3"
                stroke-linecap="round"
                stroke-linejoin="round"
              >
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </span>
            {{ opt.label }}
          </div>

          <div v-if="filteredOptions.length === 0" class="cselect__empty">
            No results found
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'ChatbotSelect',
  props: {
    value: {
      default: null
    },
    options: {
      type: Array,
      default: () => []
    },
    placeholder: {
      type: String,
      default: 'Select…'
    },
    disabled: {
      type: Boolean,
      default: false
    },
    searchable: {
      type: Boolean,
      default: false
    },
    reduce: {
      type: Function,
      default: (o) => o
    },
    label: {
      type: String,
      default: 'label'
    }
  },
  data() {
    return {
      isOpen: false,
      searchQuery: ''
    };
  },
  computed: {
    normalizedOptions() {
      return this.options.map((opt) => {
        if (typeof opt === 'object' && opt !== null) {
          const val = this.reduce(opt);
          return {
            value: val,
            label: String(opt[this.label] ?? val)
          };
        }
        return { value: opt, label: String(opt) };
      });
    },
    filteredOptions() {
      if (!this.searchable || !this.searchQuery.trim()) {
        return this.normalizedOptions;
      }
      const q = this.searchQuery.trim().toLowerCase();
      return this.normalizedOptions.filter((o) =>
        o.label.toLowerCase().includes(q)
      );
    },
    hasValue() {
      return this.value !== null && this.value !== undefined && this.value !== '';
    },
    displayLabel() {
      if (!this.hasValue) return this.placeholder;
      const match = this.normalizedOptions.find(
        (o) => String(o.value) === String(this.value)
      );
      return match ? match.label : String(this.value);
    }
  },
  mounted() {
    document.addEventListener('click', this.onOutsideClick);
  },
  beforeDestroy() {
    document.removeEventListener('click', this.onOutsideClick);
  },
  methods: {
    toggle() {
      if (this.disabled) return;
      this.isOpen = !this.isOpen;
      if (this.isOpen && this.searchable) {
        this.$nextTick(() => {
          if (this.$refs.searchInput) this.$refs.searchInput.focus();
        });
      }
      if (!this.isOpen) this.searchQuery = '';
    },
    close() {
      this.isOpen = false;
      this.searchQuery = '';
    },
    selectOption(opt) {
      this.$emit('input', opt.value);
      this.close();
    },
    isSelected(val) {
      return this.hasValue && String(this.value) === String(val);
    },
    onOutsideClick(e) {
      if (this.$refs.wrapper && !this.$refs.wrapper.contains(e.target)) {
        this.close();
      }
    },
    onKeydown(e) {
      if (e.key === 'Escape') this.close();
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        this.toggle();
      }
    }
  }
};
</script>

<style lang="scss" scoped>
.cselect {
  position: relative;
  width: 100%;

  &--disabled {
    opacity: 0.6;
    pointer-events: none;
  }
}

.cselect__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: 0.625rem 0.875rem;
  border: 1px solid #d3d9e4;
  border-radius: 8px;
  font-size: 0.95rem;
  font-weight: 500;
  color: #121926;
  background: #ffffff;
  cursor: pointer;
  text-align: left;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;

  .cselect--open & {
    border-color: #0f9d58;
    box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.16);
  }

  &:focus {
    outline: none;
    border-color: #0f9d58;
    box-shadow: 0 0 0 3px rgba(15, 157, 88, 0.16);
  }
}

.cselect__value {
  flex: 1;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;

  &--placeholder {
    color: #98a2b3;
    font-weight: 400;
  }
}

.cselect__icon {
  display: flex;
  align-items: center;
  color: #667085;
  margin-left: 0.5rem;
  flex-shrink: 0;
  transition: transform 0.18s ease;

  .cselect--open & {
    transform: rotate(180deg);
  }
}

.cselect__menu {
  position: absolute;
  top: calc(100% + 4px);
  left: 0;
  right: 0;
  z-index: 200;
  background: #ffffff;
  border: 1px solid #d3d9e4;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.10);
  overflow: hidden;
}

.cselect__search-wrap {
  padding: 8px 8px 4px;
  border-bottom: 1px solid #f2f4f7;
}

.cselect__search {
  width: 100%;
  padding: 0.4rem 0.75rem;
  border: 1px solid #d3d9e4;
  border-radius: 6px;
  font-size: 0.875rem;
  color: #121926;
  background: #f9fafb;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: #0f9d58;
  }

  &::placeholder {
    color: #98a2b3;
  }
}

.cselect__options {
  max-height: 220px;
  overflow-y: auto;
  padding: 4px 0;

  &::-webkit-scrollbar {
    width: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: #d3d9e4;
    border-radius: 2px;
  }
}

.cselect__option {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0.5rem 0.875rem;
  font-size: 0.9rem;
  font-weight: 500;
  color: #344054;
  cursor: pointer;
  transition: background 0.12s ease;

  &:hover {
    background: #f5f9f7;
  }

  &--selected {
    color: #0f9d58;
    background: #f0faf5;
  }
}

.cselect__option-check {
  width: 14px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  color: #0f9d58;
}

.cselect__empty {
  padding: 0.75rem 0.875rem;
  font-size: 0.875rem;
  color: #98a2b3;
  text-align: center;
}

.cselect-drop-enter-active,
.cselect-drop-leave-active {
  transition: opacity 0.15s ease, transform 0.15s ease;
}
.cselect-drop-enter,
.cselect-drop-leave-to {
  opacity: 0;
  transform: translateY(-4px);
}
</style>
